import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Ruler, Plus, Trash2, Pencil, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import MultiImageUploader from "../common/MultiImageUploader";

export default function MeasurementsTab() {
  const [types, setTypes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [t, p] = await Promise.all([
        api.get("/clothing-types"),
        api.get("/measurements/profiles"),
      ]);
      setTypes(t.data || []);
      setProfiles(p.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load measurements");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const typeById = useMemo(
    () => Object.fromEntries(types.map((t) => [t.id, t])),
    [types],
  );

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setDialogOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try {
      await api.delete(`/measurements/profiles/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="measurements-loading">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-24 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-dashed border-[hsl(var(--kb-border))] p-8 text-center text-sm text-red-700" data-testid="measurements-error">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="measurements-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="text-sm text-[hsl(var(--kb-muted))] max-w-xl">
          Save your measurements once and reuse them at checkout, or request an in-shop / home / video appointment and let our team take them for you.
        </div>
        <button className="kb-btn-primary inline-flex items-center gap-2" onClick={openNew} data-testid="add-measurement-btn">
          <Plus size={14} /> Add Measurement
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className="border border-dashed border-[hsl(var(--kb-border))] py-14 text-center" data-testid="measurements-empty">
          <Ruler size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-3" />
          <div className="font-display text-xl mb-1">No measurements saved yet</div>
          <div className="text-sm text-[hsl(var(--kb-muted))] mb-4">Save a profile per clothing type (blouse, kurta, shirt…) and reuse it on any stitched product.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {profiles.map((p) => {
            const t = typeById[p.clothing_type_id];
            return (
              <div
                key={p.id}
                className="border border-[hsl(var(--kb-border))] p-5"
                data-testid={`measurement-card-${p.id}`}
              >
                <div className="flex items-start justify-between mb-1 gap-2">
                  <div>
                    <div className="overline mb-1">{t?.name || "—"}</div>
                    <div className="font-display text-xl flex items-center gap-2">
                      {p.profile_name}
                      {p.is_default && (
                        <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--kb-primary))] flex items-center gap-1">
                          <Star size={10} /> default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))]" data-testid={`edit-measurement-${p.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p.id)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))] text-red-700" data-testid={`delete-measurement-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-[hsl(var(--kb-muted))] mb-3">Unit: {p.unit} · Source: {p.source}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {(p.values || []).slice(0, 8).map((v, i) => (
                    <div key={i} className="flex justify-between border-b border-[hsl(var(--kb-border))]/40 py-0.5">
                      <span className="text-[hsl(var(--kb-muted))]">{v.label}</span>
                      <span>{v.value} {p.unit}</span>
                    </div>
                  ))}
                </div>
                {p.notes && <div className="text-xs text-[hsl(var(--kb-muted))] mt-3 italic">"{p.notes}"</div>}
              </div>
            );
          })}
        </div>
      )}

      <MeasurementDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        types={types}
        editing={editing}
        onSaved={() => { setDialogOpen(false); load(); }}
      />
    </div>
  );
}

function MeasurementDialog({ open, onClose, types, editing, onSaved }) {
  const [clothingTypeId, setClothingTypeId] = useState("");
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [form, setForm] = useState({ profile_name: "", unit: "inches", notes: "", is_default: false });
  const [values, setValues] = useState({});  // key -> string
  const [referenceImages, setReferenceImages] = useState([]);
  const [saving, setSaving] = useState(false);

  // Seed on open
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setClothingTypeId(editing.clothing_type_id);
      setForm({
        profile_name: editing.profile_name,
        unit: editing.unit || "inches",
        notes: editing.notes || "",
        is_default: !!editing.is_default,
      });
      const v = {};
      (editing.values || []).forEach((x) => { v[x.key] = x.value; });
      setValues(v);
      setReferenceImages(editing.reference_images || []);
    } else {
      const first = types[0]?.id || "";
      setClothingTypeId(first);
      setForm({ profile_name: "", unit: "inches", notes: "", is_default: false });
      setValues({});
      setReferenceImages([]);
    }
  }, [open, editing, types]);

  useEffect(() => {
    if (!open || !clothingTypeId) { setFields([]); return; }
    const t = types.find((x) => x.id === clothingTypeId);
    if (!t) return;
    setFieldsLoading(true);
    (async () => {
      try {
        const r = await api.get(`/clothing-types/${t.slug}/fields`);
        setFields(r.data || []);
      } catch {
        setFields([]);
      } finally {
        setFieldsLoading(false);
      }
    })();
  }, [open, clothingTypeId, types]);

  const save = async () => {
    if (!form.profile_name.trim()) return toast.error("Profile name required");
    if (!clothingTypeId) return toast.error("Select a clothing type");
    const payload = {
      clothing_type_id: clothingTypeId,
      profile_name: form.profile_name.trim(),
      unit: form.unit,
      notes: form.notes.trim() || null,
      is_default: form.is_default,
      reference_images: referenceImages,
      values: fields
        .filter((f) => values[f.key] !== undefined && values[f.key] !== "")
        .map((f) => ({ key: f.key, value: values[f.key], unit: form.unit })),
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/measurements/profiles/${editing.id}`, payload);
      else await api.post("/measurements/profiles", payload);
      toast.success(editing ? "Updated" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="measurement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing ? "Edit measurement" : "New measurement profile"}
          </DialogTitle>
          <DialogDescription>
            Select a clothing type and fill in the measurements. Required fields are marked with a red dot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Clothing type</label>
              <select
                className="kb-input"
                value={clothingTypeId}
                onChange={(e) => setClothingTypeId(e.target.value)}
                disabled={!!editing}
                data-testid="measurement-clothing-type"
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Unit</label>
              <select
                className="kb-input"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                data-testid="measurement-unit"
              >
                <option value="inches">Inches</option>
                <option value="cm">Centimeters</option>
              </select>
            </div>
          </div>

          <div>
            <label className="overline block mb-1">Profile name</label>
            <input
              className="kb-input"
              placeholder="e.g. Mine, Mom's, Sister"
              maxLength={80}
              value={form.profile_name}
              onChange={(e) => setForm({ ...form, profile_name: e.target.value })}
              data-testid="measurement-profile-name"
            />
          </div>

          <div className="border-t pt-4">
            <div className="overline mb-3">Measurements</div>
            {fieldsLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
                ))}
              </div>
            ) : fields.length === 0 ? (
              <div className="text-sm text-[hsl(var(--kb-muted))]">No fields defined for this type.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.id}>
                    <label className="text-xs flex items-center gap-1 mb-1 text-[hsl(var(--kb-muted))]">
                      {f.label}
                      {f.is_required && <span className="text-red-600">•</span>}
                    </label>
                    <input
                      type={f.input_type === "number" ? "number" : "text"}
                      step="0.1"
                      min={f.min_value ?? undefined}
                      max={f.max_value ?? undefined}
                      className="kb-input"
                      value={values[f.key] ?? ""}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      data-testid={`measurement-field-${f.key}`}
                      placeholder={`${f.min_value ?? ""}–${f.max_value ?? ""} ${form.unit}`}
                    />
                    {f.help_text && (
                      <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-0.5">{f.help_text}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="overline block mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              className="kb-input"
              maxLength={500}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. slightly loose fit preferred"
            />
          </div>

          <div>
            <label className="overline block mb-2">Reference photos (optional)</label>
            <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
              Upload a body-shape or inspiration photo. Your seller uses these alongside measurements to tailor the perfect fit.
            </p>
            <MultiImageUploader
              value={referenceImages}
              onChange={setReferenceImages}
              maxCount={3}
              testid="measurement-reference-uploader"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              data-testid="measurement-default"
            />
            Set as default for this clothing type
          </label>
        </div>

        <DialogFooter>
          <button className="kb-btn-outline" onClick={onClose} type="button">Cancel</button>
          <button className="kb-btn-primary" onClick={save} disabled={saving} data-testid="measurement-save-btn" type="button">
            {saving ? "Saving…" : editing ? "Update" : "Create profile"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
